import React, {
  ReactElement,
  useState,
  useContext,
  createContext,
  Dispatch,
  SetStateAction,
  useEffect,
} from 'react';
import { LOCAL_STORAGE_RESERVATION } from 'constants/local-storage';
import { getItem, setItem } from 'helpers/local-storage';
import { passengerDeclarationsDefault } from 'scenes/reservation/guest-information/guest-information';
import { BOOKING_PAGES } from 'constants/booking';
import { useLocation } from 'react-router';
import { getPathWithoutLocale } from 'helpers/path-helpers';
import { BOOKING_PARKING_PATH, BOOKING_SUMMARY_PATH } from 'constants/paths';
import { parseJsonData } from 'helpers/utils';
import { CouponDTO } from 'types/models/coupons';

interface UseReservations {
  parkId: number;
  startDate: string;
  endDate: string;
  services: AncillarySlots;
  passengerDeclarations: PassengerDeclaration[];
  tempUser: any;
  slot: number;
  slotFee: number;
  slotFeeOveride: number;
  isPersonBasedPricing: boolean;
  slotName: string;
  parkToReserve: Park;
  costBreakdown: ReservationCostBreakdown;
  guests: ReservationGuestCount[];
  numberOfPassengers: number;
  page: number;
  appliedCoupon: CouponDTO | null;
  setParkId: Dispatch<SetStateAction<number>>;
  setStartDate: Dispatch<SetStateAction<string>>;
  setEndDate: Dispatch<SetStateAction<string>>;
  setServices: Dispatch<SetStateAction<AncillarySlots>>;
  setTempUser: Dispatch<SetStateAction<any>>;
  setPassengerDeclarations: Dispatch<SetStateAction<PassengerDeclaration[]>>;
  setSlot: Dispatch<SetStateAction<any>>;
  setSlotFee: Dispatch<SetStateAction<any>>;
  setSlotFeeOveride: Dispatch<SetStateAction<any>>;
  setIsPersonBasedPricing: Dispatch<SetStateAction<any>>;
  setSlotName: Dispatch<SetStateAction<string>>;
  setGuests: Dispatch<SetStateAction<ReservationGuestCount[]>>;
  setParkToReserve: Dispatch<SetStateAction<Park>>;
  setCostBreakdown: Dispatch<SetStateAction<ReservationCostBreakdown>>;
  setNumberOfPassengers: Dispatch<SetStateAction<number>>;
  setPage: Dispatch<SetStateAction<number>>;
  setAppliedCoupon: Dispatch<SetStateAction<CouponDTO | null>>;
}

const ReservationsContext = createContext({});

const ReservationsProvider = (props: any): ReactElement => {
  const location = useLocation();
  const costDefault = {
    base: {
      parkCost: 0,
      numberOfNights: 0,
      total: 0,
    },
    additionalGuests: {
      total: 0,
    },
    grandTotal: 0,
    slotCategory: {
      numberOfNights: 0,
      cost: 0,
      total: 0,
    },
  };

  const reservation = parseJsonData(getItem(LOCAL_STORAGE_RESERVATION));

  const defaultUser =
    reservation.tempUser && Object.keys(reservation.tempUser).length > 0
      ? reservation.tempUser
      : undefined;
  const [parkId, setParkId] = useState(reservation.parkId ?? 0);
  const [startDate, setStartDate] = useState(reservation.startDate ?? '');
  const [endDate, setEndDate] = useState(reservation.endDate ?? '');
  const [services, setServices] = useState(reservation.services ?? {});
  const [passengerDeclarations, setPassengerDeclarations] = useState(
    reservation.passengerDeclarations ?? [passengerDeclarationsDefault],
  );
  const [tempUser, setTempUser] = useState(defaultUser);
  const [slotName, setSlotName] = useState(reservation.slotName ?? '');
  const [slot, setSlot] = useState(reservation.slot ?? 0);
  const [slotFee, setSlotFee] = useState(reservation.slotFee ?? 0); // eslint-disable-line @typescript-eslint/consistent-type-assertions
  const [slotFeeOveride, setSlotFeeOveride] = useState(
    reservation.slotFeeOveride ?? 0,
  ); // eslint-disable-line @typescript-eslint/consistent-type-assertions
  const [isPersonBasedPricing, setIsPersonBasedPricing] = useState<boolean>(
    true,
  );
  const [guests, setGuests] = useState<ReservationGuestCount[]>(
    reservation.guests ?? [],
  );
  const [parkToReserve, setParkToReserve] = useState<Park>(
    reservation.parkToReserve ?? ({} as Park), // eslint-disable-line @typescript-eslint/consistent-type-assertions
  );
  const [costBreakdown, setCostBreakdown] = useState<ReservationCostBreakdown>(
    reservation.costBreakdown ?? costDefault,
  );
  const [numberOfPassengers, setNumberOfPassengers] = useState(
    reservation.numberOfPassengers ?? 1,
  );
  const [appliedCoupon, setAppliedCoupon] = useState<CouponDTO | null>(null);

  let defaultPage = BOOKING_PAGES.CONTACT_DETAILS;
  const pathname = getPathWithoutLocale(location.pathname);
  if (pathname === BOOKING_PARKING_PATH) {
    defaultPage = BOOKING_PAGES.SLOT_PICKER;
  } else if (pathname === BOOKING_SUMMARY_PATH) {
    defaultPage = BOOKING_PAGES.BOOKING_SUMMARY;
  }

  const [page, setPage] = useState(defaultPage);

  useEffect(() => {
    setItem(
      LOCAL_STORAGE_RESERVATION,
      JSON.stringify({
        parkId,
        startDate,
        endDate,
        services,
        tempUser,
        passengerDeclarations,
        slot,
        slotName,
        slotFee,
        slotFeeOveride,
        isPersonBasedPricing,
        guests,
        parkToReserve,
        costBreakdown,
        numberOfPassengers,
      }),
    );
  }, [
    parkId,
    startDate,
    endDate,
    services,
    tempUser,
    passengerDeclarations,
    slot,
    slotFee,
    slotFeeOveride,
    isPersonBasedPricing,
    slotName,
    guests,
    parkToReserve,
    costBreakdown,
    numberOfPassengers,
  ]);

  const values: UseReservations = {
    parkId,
    endDate,
    startDate,
    services,
    passengerDeclarations,
    tempUser,
    slot,
    slotFee,
    slotFeeOveride,
    isPersonBasedPricing,
    slotName,
    guests,
    parkToReserve,
    costBreakdown,
    numberOfPassengers,
    page,
    appliedCoupon,
    setParkId,
    setStartDate,
    setEndDate,
    setServices,
    setPassengerDeclarations,
    setTempUser,
    setSlot,
    setSlotFee,
    setSlotFeeOveride,
    setIsPersonBasedPricing,
    setSlotName,
    setGuests,
    setParkToReserve,
    setCostBreakdown,
    setNumberOfPassengers,
    setPage,
    setAppliedCoupon,
  };

  return <ReservationsContext.Provider value={values} {...props} />;
};

const useReservations = (): UseReservations =>
  useContext(ReservationsContext) as UseReservations;

export { ReservationsProvider, useReservations };
